import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import DynamicBreadcrumbs from "../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import { BreadcrumbContainer, CustomBreadcrumbWrapper } from "./Breadcrumb.styled";
import { useStore } from "../../../../../hooks";
import { useDrivesStores } from "../../hook/use-drives.hook";
import { useFolderHierarchy } from "../../hooks/useFolderHierarchy";

const DetailBreadcrumb = observer(() => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { breadcrumbStore } = useStore();
  const { activeFolderStore, drivesViewStore } = useDrivesStores();
  const { vaultUuid, folderHierarchy: uuidHierarchy, isDriveContext } = useFolderHierarchy();

  // ✅ PERFORMANCE: handleNavigate doesn't need useCallback unless DynamicBreadcrumbs is memoized
  // Since it's a simple function and not passed to memoized components, regular function is fine
  const handleNavigate = (href) => {
    if (href && href !== pathname) {
      navigate(href);
    }
  };

  // ✅ PERFORMANCE: Extract primitive values to avoid array/object dependencies
  const drivesLength = drivesViewStore?.drivesStore?.drives?.length ?? 0;
  const folderNamesMapKeys = Object.keys(activeFolderStore.folderNamesMap || {}).join(',');

  // ✅ Get drive name if in drive context
  const driveName = useMemo(() => {
    if (!isDriveContext || !vaultUuid || drivesLength === 0) {
      return null;
    }
    const drive = drivesViewStore.drivesStore.drives.find(d => d.id === vaultUuid);
    return drive?.title || 'Drive';
  }, [isDriveContext, vaultUuid, drivesLength]);

  useEffect(() => {
    const folderNamesMap = activeFolderStore.folderNamesMap || {};
    
    // Start with base items
    const items = [
      { label: 'AIMS Drive', href: '/v2/drives' }
    ];

    // ✅ If in drive context, add drive to breadcrumb
    if (isDriveContext && vaultUuid && driveName) {
      items.push({ label: driveName, href: `/v2/drives/d/${vaultUuid}` });
    } else {
      // Backward compatibility: add My Workspace for non-drive contexts
      items.push({ label: 'My Workspace', href: '/v2/drives/my-workspace' });
    }

    // Build breadcrumb from URL hierarchy (folders)
    if (uuidHierarchy && uuidHierarchy.length > 0) {
      uuidHierarchy.forEach((folderUuid, index) => {
        let urlPath;
        if (isDriveContext && vaultUuid) {
          // Drive context: /v2/drives/d/vaultUuid/folder1/folder2/...
          const folderPath = uuidHierarchy.slice(0, index + 1).join('/');
          urlPath = `/v2/drives/d/${vaultUuid}/${folderPath}`;
        } else {
          // Backward compatibility: /v2/drives/uuid1/uuid2/...
          urlPath = `/v2/drives/${uuidHierarchy.slice(0, index + 1).join('/')}`;
        }
        const folderName = folderNamesMap[folderUuid] || 'Loading...';
        
        items.push({
          label: folderName,
          href: urlPath
        });
      });
    }

    breadcrumbStore.setItems(items);
    breadcrumbStore.setCurrentPath(pathname);

    return () => {
      breadcrumbStore.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuidHierarchy, folderNamesMapKeys, pathname, isDriveContext, vaultUuid, driveName]);

  return (
    <BreadcrumbContainer>
      <CustomBreadcrumbWrapper>
        <DynamicBreadcrumbs 
          items={breadcrumbStore.breadcrumbItems} 
          currentPath={breadcrumbStore.currentPath}
          onNavigate={handleNavigate}
        />
      </CustomBreadcrumbWrapper>
    </BreadcrumbContainer>
  );
});

export default DetailBreadcrumb;

